import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CvDto, MasterDataDto, ProjectDto, SkillDto, UserAccountDto } from 'src/app/services/api-client/models';
import { UserAccountRestControllerService } from 'src/app/services/api-client/services';
import { UserStoreService } from 'src/app/services/user-store.service';
import { DataEnum } from 'src/app/shared/enums/data.model';
import { UnsavedDataHelper } from 'src/app/shared/unsaved-data-helper';
import { CvService } from '../cv-list/services/cv.service';
import { ScrollService } from '../../shared/services/scroll.service';

@Component({
  selector: 'app-cv-data',
  templateUrl: './cv-data.component.html',
  styleUrls: ['./cv-data.component.scss']
})
export class CvDataComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('masterData', {read: ElementRef}) masterDataSection: ElementRef;
  @ViewChild('skillData', {read: ElementRef}) skillDataSection: ElementRef;
  @ViewChild('projectData', {read: ElementRef}) projectDataSection: ElementRef;

  subscriptions = new Subscription();
  emptySkill: SkillDto = {
    branches: [],
    certificates: [{ name: null, selected: true, certificateDate: null, id: null }],
    id: null,
    itCompetences: [{ id: null, selected: true, name: null, percentageCompetence: 60 }],
    languages: [{ id: null, selected: true, name: null, languageLevel: null }],
    activities: [{ id: null, selected: true, name: null }],
    professionalCompetences: [{ id: null, name: null, selected: true, percentageCompetence: 60 }]
  };

  @Input()
  public cv: CvDto;
  @Output()
  public cvChangeHandler = new EventEmitter<CvDto>();
  @Output()
  public cvDirtyHandler = new EventEmitter<any[]>();

  public cvType: "CONSULTANT_CV" | "ORIGIN_CV";
  dirtyArrIndx: number[] = [null, null, null];
  profileDirty = false;
  masterDataChanges = 0;

  isCurrentUser = false;
  dirtyArr = [false, false, false];
  language: string;
  user = '';
  email = '';

  selectedUserData: UserAccountDto;
  isActiveRoute = new Map<string, boolean>([
    ['masterData', false],
    ['skillData', false],
    ['projectData', false]
  ])

  constructor(
    private helper: UnsavedDataHelper,
    private userAccountService: UserAccountRestControllerService,
    private activatedRoute: ActivatedRoute,
    private userStore: UserStoreService,
    private cvService: CvService,
    private scrollService: ScrollService,
  ) {
    this.scrollService.onScrolledEvent.subscribe(scrollEvent => {
      if (scrollEvent && this.masterDataSection && this.skillDataSection && this.projectDataSection ) {
        this.checkVisibleSpace();
      }
    });
  }

  checkVisibleSpace() {
    const masterDataVisibleHeight = this.getVisibleHeight(this.masterDataSection.nativeElement);
    const skillDataVisibleHeight = this.getVisibleHeight(this.skillDataSection.nativeElement);
    const projectDataVisibleHeight = this.getVisibleHeight(this.projectDataSection.nativeElement);

    const maxVisibleHeight = Math.max(masterDataVisibleHeight, skillDataVisibleHeight, projectDataVisibleHeight);

    if (maxVisibleHeight === masterDataVisibleHeight) {
      this.updateActiveRoutes('masterData');
    } else if (maxVisibleHeight === skillDataVisibleHeight) {
      this.updateActiveRoutes('skillData');
    } else if (maxVisibleHeight === projectDataVisibleHeight) {
      this.updateActiveRoutes('projectData');
    }
  }

  getVisibleHeight(element: HTMLElement): number {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const visibleHeight = Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
    return visibleHeight;
  }

  ngOnInit() {
    if (!this.activatedRoute.snapshot.params.cvId) {
      this.isCurrentUser = true;
    }
    this.subscriptions.add(this.userStore.getUser().subscribe(user => {
      if ((this.activatedRoute.snapshot.data.cv.userAccountId === user.id)) {
        this.isCurrentUser = true;
      }
    }));

    this.language = (this.cv.language === 'GERMAN') ? 'Deutsch' : 'Englisch';
    let userId = this.cv.userAccountId;

    this.subscriptions.add(this.userAccountService.getUserAccountById({id: userId}).subscribe(async(selectedUsr: UserAccountDto) => {
      this.selectedUserData = selectedUsr;
      if (selectedUsr.firstName != null && selectedUsr.lastName != null) {
        this.user = selectedUsr.firstName + ' ' + selectedUsr.lastName;
      }
      this.email = selectedUsr.email;
      })
    );

  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes?.cv?.currentValue && !changes?.cv?.firstChange) {
      this.cv = changes.cv.currentValue;
      this.cvType = this.cv.cvType;
    }
  }

  downloadDocX(cv: CvDto) {
    if (!this.profileDirty) {
      this.cvService.downloadDocX(cv);
    } else {
      this.profileDirty = !this.helper.checkDirtyElements(this.dirtyArrIndx);
      if (!this.profileDirty) {
        this.downloadDocX(cv);
      }
    }
  }

  isConsultantCV(): boolean {
    return this.cv.cvType === 'CONSULTANT_CV';
  }

  isOriginCV(): boolean {
    return this.cv.cvType === 'ORIGIN_CV';
  }

  handleMasterDataChange(masterDataDto: MasterDataDto) {
    this.cv.masterData = masterDataDto;
    this.cvChangeHandler.emit(this.cv);
  }

  handleProjectChange(projects: ProjectDto[]) {
    this.cv.projects = projects;
    this.cvChangeHandler.emit(this.cv);
  }

  handleSkillChange(skill: SkillDto) {
    this.cv.skill = skill;
    this.cvChangeHandler.emit(this.cv);
  }

  handleParsedCV(cv: CvDto) {
    if(this.cv.skill) cv.skill.id = this.cv.id;
    if(this.cv.masterData) cv.masterData.id = this.cv.id;
    this.cvChangeHandler.emit(cv);
  }

  markMasterDataDirty(changes:any){
    if (this.masterDataChanges != 0) {
      this.checkChanges(changes, DataEnum.MASTER);
    } else {
      this.masterDataChanges++;
    }
  }

  markProjectsDirty(changes:any){
    this.checkChanges(changes, DataEnum.PROJECTS);
  }

  markSkillsDirty(changes:any){
    this.checkChanges(changes, DataEnum.SKILLS);
  }

  checkChanges(changes: any, dataKind: string) {
    let changeIndex: number;
    switch(dataKind) {
      case DataEnum.MASTER: changeIndex = 0; break;
      case DataEnum.SKILLS: changeIndex = 1; break;
      case DataEnum.PROJECTS: changeIndex = 2; break;
      default: console.log('Error in method checkChanges().');
    }
    this.dirtyArr[changeIndex] = (changes) ? true : false;
    if (this.isConsultantCV()) {
      this.profileDirty = this.checkIfProfileDirty(this.dirtyArr);
    }
    this.cvDirtyHandler.emit(this.dirtyArr);
  }

  checkIfProfileDirty(arr: boolean[]): boolean {
    let res = false;
    this.dirtyArrIndx = [null, null, null];
    arr.forEach((element, indx )=> {
      if (element === true) {
        res = true;
        this.dirtyArrIndx[indx] = indx;
      }
    });
    return res;
  }

  updateActiveRoutes(newActive: string) {
    this.isActiveRoute['masterData'] = false;
    this.isActiveRoute['skillData'] = false;
    this.isActiveRoute['projectData'] = false;
    this.isActiveRoute[newActive] = true;
  }

  ngAfterViewInit() {
    setTimeout(() => this.checkVisibleSpace(), 100)
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
